import { useState } from "react";
import { ChevronDown, Mail, MailX, MailWarning, Clock3, Eye } from "lucide-react";
import { useFetch } from "@/lib/positions";
import { useLang } from "@/lib/i18n";
import { fmtDateTimeMtl } from "@/lib/time";

const STATUS = {
  sent: [Mail, "text-emerald-700 border-emerald-200 bg-emerald-50"],
  skipped: [MailX, "text-neutral-500 border-black/10"],
  failed: [MailWarning, "text-red-700 border-red-200 bg-red-50"],
  queued: [Clock3, "text-amber-700 border-amber-200 bg-amber-50"],
  paused: [Clock3, "text-amber-700 border-amber-200 bg-amber-50"],
  skipped_demo: [MailX, "text-neutral-500 border-black/10"],
};
const statusOf = (s) => (STATUS[s] ? s : "queued");

const Recipient = ({ r }) => {
  const { t } = useLang();
  const s = statusOf(r.email_status);
  const [Icon, cls] = STATUS[s];
  return (
    <li className="flex items-center justify-between gap-3 py-2 text-sm" data-testid={`notif-recipient-${r.user_id}`}>
      <span className="flex items-center gap-1.5 min-w-0"><span className="truncate">{r.name}</span></span>
      <span className="flex items-center gap-2 shrink-0">
        {r.read && <Eye className="w-3.5 h-3.5 text-neutral-400" aria-label={t("nh.read")} />}
        <span className={`inline-flex items-center gap-1 border rounded-full px-2 py-0.5 text-[11px] ${cls}`}><Icon className="w-3 h-3" />{t(`nh.${s}`)}</span>
      </span>
    </li>
  );
};

const Group = ({ g }) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const counts = g.recipients.reduce((m, r) => ({ ...m, [statusOf(r.email_status)]: (m[statusOf(r.email_status)] || 0) + 1 }), {});
  return (
    <li className="px-5 py-4" data-testid={`notif-group-${g.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-left flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 relative pr-6" data-testid={`notif-group-toggle-${g.id}`}>
        <span className="mono text-xs text-neutral-400 sm:w-36 shrink-0 pt-0.5">{fmtDateTimeMtl(g.sent_at)}</span>
        <span className="flex-1 min-w-0">
          <span className="block font-medium text-sm">{g.title}</span>
          <span className="block text-xs text-neutral-500 mt-1 line-clamp-2">{g.body}</span>
          <span className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[11px] text-neutral-500">
            <span data-testid={`notif-group-count-${g.id}`}>{g.recipients.length} {t("nh.recipients")}</span>
            {Object.entries(counts).map(([k, v]) => <span key={k}>· {v} {t(`nh.${k}`).toLowerCase()}</span>)}
          </span>
        </span>
        <ChevronDown className={`w-4 h-4 text-neutral-400 shrink-0 absolute right-0 top-0.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 sm:ml-40">
          <p className="text-sm text-neutral-600 whitespace-pre-line mb-2" data-testid={`notif-group-body-${g.id}`}>{g.body}</p>
          <ul className="divide-y divide-black/[0.05] border-t border-black/[0.05]">{g.recipients.map((r) => <Recipient key={r.user_id} r={r} />)}</ul>
        </div>
      )}
    </li>
  );
};

export const EventNotifications = ({ eventId }) => {
  const { t } = useLang();
  const { data } = useFetch(`/events/${eventId}/notifications`);
  if (!data) return null;
  if (!data.length) return <p className="text-sm text-neutral-500" data-testid="event-notifications-empty">{t("nh.empty")}</p>;
  return <ol className="card-lux divide-y divide-black/[0.05]" data-testid="event-notifications">{data.map((g) => <Group key={g.id} g={g} />)}</ol>;
};
